/* eslint-disable react-hooks/rules-of-hooks */
import {
  Rectangle2d,
  SVGContainer,
  ShapeUtil,
  SvgExportContext,
  T,
  TLOnTranslateHandler,
  TLShape,
  toDomPrecision,
} from '@tldraw/tldraw'
import Styles from './FrameShape.module.scss'
import { FrameShape, FrameShapeProps } from './Types'
import { FillStyle } from 'components/VisualStudioPlayground/VisualEditor/StyleProps'
import { frameShapeMigrations } from './FrameShape'
import { CustomEqualitySet } from 'components/VisualStudioPlayground/VisualEditor/utils/customEqualitySet'
import { GradientColor, SolidColor } from 'components/VisualStudioPlayground/VisualEditor/Types'
import { isEqual } from 'lodash'
import { FrameSidebar } from './FrameSidebar'
import { getFillGradient } from '../GeoShape/getFillGradient'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { FrameSkin } from './FrameSkin/FrameSkin'
import { useRootStore } from 'stores/RootStore'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { getBackgroundImage, getIsPreview, getOverlap } from 'components/VisualStudioPlayground/VisualEditor/helpers'

export class FrameShapeUtil extends ShapeUtil<FrameShape> {
  static override type = 'frame' as const

  static override migrations = frameShapeMigrations

  static override props = {
    w: T.positiveNumber,
    h: T.positiveNumber,
    position: T.positiveNumber,
    name: T.string,
    caption: T.nullable(T.string),
    fill: FillStyle,
    isEditable: T.boolean,
    isAutoUpdatable: T.boolean,
  }

  getGeometry(shape: FrameShape) {
    return new Rectangle2d({
      width: shape.props.w,
      height: shape.props.h,
      isFilled: true,
    })
  }

  getDefaultProps(): FrameShapeProps {
    return {
      w: 1080,
      h: 1080,
      position: 1,
      name: '',
      caption: null,
      fill: { type: 'solidColor', color: '#FFFFFF', opacity: 1 },
      isEditable: true,
      isAutoUpdatable: true,
    }
  }

  component(shape: FrameShape) {
    const bounds = this.editor.getShapeGeometry(shape).bounds
    const { id } = shape
    const { fill, w, h, position } = shape.props
    const isReadonly = this.editor.getInstanceState().isReadonly
    const fillGradient = getFillGradient(shape)
    const { designStore } = useRootStore()
    const isPreview = getIsPreview(this.editor)

    return (
      <ConditionalWrapper
        condition={position === 1 && !isReadonly && !isPreview}
        wrapper={(children) => (
          <FrameSkin width={w} height={h} channel={designStore?.file?.channel}>
            {children}
          </FrameSkin>
        )}
      >
        {!isReadonly && (
          <ResponsiveDesign.Desktop>
            <FrameSidebar id={id} />
          </ResponsiveDesign.Desktop>
        )}
        <div className={Styles.card}>
          <div className={Styles.frame} style={{ height: h, width: w }}>
            <SVGContainer id={id}>
              <rect
                width={bounds.width}
                height={bounds.height}
                style={{
                  fill: fill.type === 'solidColor' ? fill.color : `url('#${fillGradient.id}')`,
                  opacity: fill.type === 'solidColor' ? fill.opacity : 1,
                  transition: 'all 250ms ease',
                }}
              />
              <defs>{fillGradient.defs}</defs>
            </SVGContainer>
          </div>
        </div>
      </ConditionalWrapper>
    )
  }

  indicator(shape: FrameShape) {
    const bounds = this.editor.getShapeGeometry(shape).bounds
    return <rect width={toDomPrecision(bounds.width)} height={toDomPrecision(bounds.height)} />
  }

  onDragShapesOver = (frame: FrameShape, shapes: TLShape[]): { shouldHint: boolean } => {
    if (!shapes.every((child) => child.parentId === frame.id)) {
      this.editor.reparentShapes(
        shapes.map((shape) => shape.id),
        frame.id,
      )
      return { shouldHint: true }
    }
    return { shouldHint: false }
  }

  isColorisable = (frame: FrameShape) => {
    const backgroundImage = getBackgroundImage(this.editor, frame.id)

    return !backgroundImage || getOverlap(this.editor, backgroundImage, frame) !== frame.props.w * frame.props.h
  }

  onBeforeUpdate = (prev: FrameShape, next: FrameShape) => {
    if (next.rotation !== prev.rotation) return prev
    if (!prev.props.isEditable) {
      return { ...prev, props: { ...prev.props, isEditable: next.props.isEditable } }
    }

    let fill = next.props.fill

    if (next.opacity !== prev.opacity) {
      switch (fill.type) {
        case 'solidColor':
          fill = { ...fill, opacity: next.opacity }
          break
        case 'linearGradient':
          fill = { ...fill, stops: fill.stops.map((stop) => ({ ...stop, opacity: next.opacity })) }
          break
      }
    }

    return {
      ...prev,
      y: next.y,
      meta: {
        ...prev.meta,
      },
      props: {
        ...prev.props,
        name: next.props.name,
        position: next.props.position,
        fill,
        caption: next.props.caption,
        h: next.props.h,
        w: next.props.w,
      },
    }
  }

  getOpacity = (shape: FrameShape) => {
    switch (shape.props.fill.type) {
      case 'solidColor':
        return shape.props.fill.opacity
      case 'linearGradient':
        return shape.props.fill.stops[0].opacity
    }
  }

  onTranslate: TLOnTranslateHandler<FrameShape> = (initialShape) => {
    if (!initialShape.props.isEditable) {
      this.editor.snaps.clear()
    }
  }

  override toSvg(
    shape: FrameShape,
    ctx: SvgExportContext,
    addDefs?: (defsHtmlString: string) => void,
    addCoords?: boolean,
  ) {
    const g = document.createElementNS('http://www.w3.org/2000/svg', 'g')

    const { x, y } = shape
    const { w, h, fill } = shape.props

    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
    rect.setAttribute('width', w.toString())
    rect.setAttribute('height', h.toString())
    if (fill.type === 'linearGradient') {
      const { id, defsHtmlString, svgExportDef } = getFillGradient(shape)
      rect.setAttribute('fill', `url(#${id})`)
      ctx.addExportDef(svgExportDef)
      if (defsHtmlString && addDefs) addDefs(defsHtmlString)
    } else {
      rect.setAttribute('fill', fill.color)
      rect.setAttribute('fill-opacity', `${fill.opacity}`)
    }
    rect.setAttribute('rx', '1')
    rect.setAttribute('ry', '1')
    if (addCoords) {
      rect.setAttribute('x', x.toString())
      rect.setAttribute('y', y.toString())
    }
    rect.setAttribute('data-shape-type', 'frame')
    g.appendChild(rect)

    return g
  }

  getColors = (shape: FrameShape) => {
    const colorSet = new CustomEqualitySet<SolidColor | GradientColor>(isEqual)

    if (this.isColorisable(shape)) colorSet.add(shape.props.fill)

    return colorSet
  }

  /**
   * Checks if the frame is blank. A frame is considered blank if it has no
   * children and a white background.
   */
  isBlank = (shape: FrameShape) => {
    return (
      this.editor.getSortedChildIdsForParent(shape).length === 0 &&
      shape.props.fill.type === 'solidColor' &&
      !!shape.props.fill.color.match(/^#fff/i)
    )
  }

  override onDragShapesOut = () => {}
  canReceiveNewChildrenOfType(_shape: FrameShape, type: string): boolean {
    if (type === 'frame') return false
    return true
  }
  override canDropShapes = () => true
  override canSnap = () => true
  override canScroll = () => false
  override canUnmount = () => false
  override canBind = () => false
  override canEdit = () => false
  override canResize = () => false
  override canEditInReadOnly = () => false
  override canCrop = () => false
  override hideResizeHandles = () => true
  override hideRotateHandle = () => true
  override hideSelectionBoundsBg = () => true
  override hideSelectionBoundsFg = () => true
  override isAspectRatioLocked = () => true
  override onTranslateStart = (shape: FrameShape) => shape
  override onTranslateEnd = (shape: FrameShape) => shape
  override onDoubleClick = (shape: FrameShape) => shape
  override onDoubleClickEdge = (shape: FrameShape) => shape
}
